import { CSCompensation } from "@components/features/CSCompensation";
import CSCompensationDialog from "@components/features/CSCompensation/CSCompensationDialog";
import { MegaShare } from "@components/features/MegaShare";
import MegaShareFriendDialog from "@components/features/MegaShare/MegaShareFriendDialog";
import { SkinnerBox } from "@components/features/SkinnerBox";
import { Paths } from "@constants";
import useCSCompensation from "@hooks/useCSCompensation";
import { useMegaShareStatus } from "@hooks/useMegaShareStatus";
import useSkinnerBoxStatus from "@hooks/useSkinnerBoxStatus";
import { getThemeAsset } from "@utils/theme";
import { MegaShareState } from "@views/PromotionMegaSharePage/types";
import { PropsWithClassName, cn } from "kz-ui-sdk";
import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";

interface PromotionFABGroupProps extends PropsWithClassName {}

const excludedRoutes = [
  Paths.PRIVATE.PROMOTION_DAILY_LOGIN,
  Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN,
  Paths.PRIVATE.PROMOTION_REFER_FRIEND,
  Paths.PRIVATE.PROMOTION_CASHBACK,
  Paths.PRIVATE.PROMOTION_REFER_BALLS,
  Paths.PRIVATE.PROMOTION_SHARE_AND_EARN,
  Paths.PRIVATE.PROMOTION_SECRET_CODE,
  Paths.PRIVATE.PROMOTION_REDEEM_SECRET_CODE,
];

const PromotionFABGroup = ({ className }: PromotionFABGroupProps) => {
  const location = useLocation();
  const { shouldShowCSCompensation, openCSCompensation } = useCSCompensation();
  const {
    shouldShowTakerMegaShare,
    taker: megaShareTaker,
    details: megaShareDetails,
    openFriendDialog: openMegaShareFriendDialog,
  } = useMegaShareStatus();
  const { shouldShowSkinnerBox, skinnerBoxClaimableAmount, skinnerBoxKey, handleLeaveAnimationCompleted } =
    useSkinnerBoxStatus();

  const megaShareStatus = megaShareTaker?.status;
  const megaShareClaimAmount = megaShareDetails?.takerClaimAmount;

  const isExcludedRoute = useMemo(
    () => excludedRoutes.some((route) => matchPath(route, location.pathname)),
    [location.pathname],
  );

  const shouldShowCSCompensationFAB = !isExcludedRoute && shouldShowCSCompensation;

  const shouldShowMegaShareFAB =
    !isExcludedRoute &&
    shouldShowTakerMegaShare &&
    megaShareStatus === MegaShareState.CLAIMABLE &&
    !!megaShareClaimAmount &&
    megaShareClaimAmount > 0;

  const shouldShowSkinnerFAB = shouldShowSkinnerBox && !isExcludedRoute && !!skinnerBoxClaimableAmount;

  if (!shouldShowCSCompensationFAB && !shouldShowMegaShareFAB && !shouldShowSkinnerFAB) {
    return null;
  }

  const promotionBackground = getThemeAsset("/skinner-box/dark-background.png");

  const shouldShowBackground = shouldShowCSCompensationFAB || shouldShowSkinnerFAB || shouldShowMegaShareFAB;
  return (
    <div className={cn("fixed bottom-[56px] end-1/2 z-[5] w-full max-w-md translate-start-1/2", className)}>
      <div className="absolute bottom-0 end-0 flex w-auto max-w-md flex-col items-center">
        {shouldShowBackground && (
          <img
            src={promotionBackground}
            alt="Promotion background"
            className="invert-x absolute bottom-0 end-0"
          />
        )}

        {shouldShowCSCompensationFAB && <CSCompensation onClick={openCSCompensation} />}
        {shouldShowSkinnerFAB && (
          <SkinnerBox
            key={skinnerBoxKey}
            claimableAmount={skinnerBoxClaimableAmount}
            onLeaveAnimationCompleted={handleLeaveAnimationCompleted}
          />
        )}
        {shouldShowMegaShareFAB && <MegaShare onClick={openMegaShareFriendDialog} />}
      </div>

      {/* Mount dialog once; opens via custom event */}
      <CSCompensationDialog />

      <MegaShareFriendDialog />
    </div>
  );
};

export default PromotionFABGroup;
